import React, { useEffect, useState } from 'react';
import {
    FLANKER_DIRECTIONS,
    FLANKER_ICON_BY_DIRECTION,
    createFlankerRound,
    BrainTrainingGameIcon,
    GameResultOverlay,
    GameShell,
} from '../shared';
import { getDirectionLabel } from './gameCopy';

const LEVEL_UP_STREAK = 2;

function FlankerDirectionGame({ copy = {}, initialLevel = 1, ...props }) {
    const startLevel = Math.max(1, Math.min(16, Math.round(Number(initialLevel) || 1)));
    const [round, setRound] = useState(1);
    const [difficulty, setDifficulty] = useState(startLevel);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [trial, setTrial] = useState(() => createFlankerRound(startLevel));
    const [choice, setChoice] = useState('');
    const maxRounds = 16;
    const done = round > maxRounds;

    const reset = () => {
        setRound(1);
        setDifficulty(startLevel);
        setScore(0);
        setStreak(0);
        setTrial(createFlankerRound(startLevel));
        setChoice('');
    };

    const choose = (direction) => {
        if (done || choice) {
            return;
        }

        const correct = direction === trial.target;
        const nextStreak = correct ? streak + 1 : 0;
        setScore((value) => value + (correct ? 1 : 0));
        setStreak(nextStreak);
        setDifficulty((current) => (
            correct && nextStreak % LEVEL_UP_STREAK === 0
                ? Math.min(16, current + 1)
                : current
        ));
        setChoice(direction);
    };

    const nextRound = () => {
        const nextRound = round + 1;
        setRound(nextRound);
        setChoice('');
        if (nextRound <= maxRounds) {
            setTrial(createFlankerRound(difficulty));
        }
    };

    useEffect(() => {
        if (!choice || done) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            nextRound();
        }, 560);

        return () => window.clearTimeout(timer);
    }, [choice, done]);

    const correctChoice = choice === trial.target;

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level: difficulty, maxScore: maxRounds, score, streak }}
            tone={correctChoice || done ? 'good' : choice ? 'miss' : 'ready'}
        >
            <div className="UFOV-brain-games__flanker-game">
                <div className="UFOV-brain-games__flanker-row" aria-label={copy.flankerArrows}>
                    {trial.arrows.map((direction, index) => (
                        <span
                            className="UFOV-brain-games__flanker-arrow"
                            key={`${trial.id}-${index}`}
                            aria-hidden="true"
                        >
                            <BrainTrainingGameIcon icon={FLANKER_ICON_BY_DIRECTION[direction]} />
                        </span>
                    ))}
                </div>
                <div className="UFOV-brain-games__flanker-actions">
                    {FLANKER_DIRECTIONS.map((direction) => (
                        <button
                            aria-label={getDirectionLabel(copy, direction)}
                            className={`UFOV-brain-games__flanker-button is-${direction} ${choice === direction ? 'is-picked' : ''} ${choice && trial.target === direction ? 'is-correct' : ''}`.trim()}
                            disabled={done || Boolean(choice)}
                            key={direction}
                            onClick={() => choose(direction)}
                            type="button"
                        >
                            <BrainTrainingGameIcon icon={FLANKER_ICON_BY_DIRECTION[direction]} />
                        </button>
                    ))}
                </div>
                <div className="UFOV-brain-games__game-cue">{copy.flankerCue}</div>
            </div>
            {done ? (
                <GameResultOverlay
                    metrics={{ level: difficulty, maxScore: maxRounds, score, streak }}
                    onRestart={reset}
                />
            ) : null}
        </GameShell>
    );
}

export default FlankerDirectionGame;
